import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Grid,
  Paper,
  Collapse,
  Button,
} from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import GlassCard from "../../../components/common/GlassCard";
import BillingSuggestions from "./BillingSuggestions";

function getBillingStatusColor(status) {
  switch (status?.toLowerCase()) {
    case "paid":
      return "success";
    case "pending":
      return "warning";
    case "submitted":
      return "info";
    case "denied":
      return "error";
    case "appealed":
      return "warning";
    default:
      return "default";
  }
}

function BillingRecordsCard({ billingRecords, onDeleteBillingRecord, onAddBillingRecord, apiBase, patientId, onBillingAdded }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeBillingId, setActiveBillingId] = useState(null);
  const [expandAll, setExpandAll] = useState(false);

  const handleMenuOpen = (event, id) => {
    setMenuAnchor(event.currentTarget);
    setActiveBillingId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveBillingId(null);
  };

  const handleDelete = (billingId) => {
    handleMenuClose();
    if (onDeleteBillingRecord) {
      onDeleteBillingRecord(billingId);
    }
  };

  const sortedRecords = billingRecords
    ? [...billingRecords].sort((a, b) => {
        const dateA = new Date(a.service_date || 0);
        const dateB = new Date(b.service_date || 0);
        return dateB - dateA; // Most recent first
      })
    : [];

  // Calculate totals
  const totalAmount = sortedRecords.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const paidAmount = sortedRecords
    .filter((r) => r.status?.toLowerCase() === "paid")
    .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const pendingAmount = sortedRecords
    .filter((r) => r.status?.toLowerCase() === "pending")
    .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

  return (
    <GlassCard
      title="Billing Records"
      addLabel="Add Billing Record"
      icon={<AttachMoneyIcon color="success" />}
      onAdd={onAddBillingRecord}
      data-testid="billing-records-card"
      data-card-category="billing_records"
    >
      {apiBase && patientId && (
        <BillingSuggestions apiBase={apiBase} patientId={patientId} billingRecords={billingRecords} onAdded={onBillingAdded} />
      )}
      {/* Summary */}
      {sortedRecords?.length > 0 && (
        <Grid container spacing={1} mb={2} sx={{ p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          <Grid item xs={4}>
            <Typography
              variant="caption"
              color="info.dark"
              fontWeight={600}
              data-testid="billing-total-amount"
              data-field-name="total_amount"
            >
              Total: ${totalAmount.toFixed(2)}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography
              variant="caption"
              color="success.dark"
              fontWeight={600}
              data-testid="billing-paid-amount"
              data-field-name="paid_amount"
            >
              Paid: ${paidAmount.toFixed(2)}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography
              variant="caption"
              color="warning.dark"
              fontWeight={600}
              data-testid="billing-pending-amount"
              data-field-name="pending_amount"
            >
              Pending: ${pendingAmount.toFixed(2)}
            </Typography>
          </Grid>
        </Grid>
      )}

      {sortedRecords?.length ? (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            {sortedRecords.slice(0, 2).map((record) => (
              <Paper
                key={record.id}
                sx={{
                  p: 1.25,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
                data-testid={`billing-item-${record.id}`}
                data-service-code={record.service_code}
                data-billing-status={record.status}
                data-service-date={record.service_date}
                data-provider={record.provider}
                data-amount={record.amount}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={600}>
                      {record.service_code || "Unknown Code"} • ${parseFloat(record.amount || 0).toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {record.description || "Service"} • {record.service_date ? record.service_date.split("T")[0] : "N/A"}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={0.5} alignItems="center">
                    <Chip
                      size="small"
                      label={record.status || "pending"}
                      color={getBillingStatusColor(record.status)}
                      variant="outlined"
                      data-testid={`billing-status-chip-${record.id}`}
                      data-field-name="status"
                    />
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, record.id)}
                        data-testid={`billing-menu-${record.id}`}
                        aria-label="Billing record options menu"
                      >
                        <MoreVertIcon aria-label="Billing record options menu" fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>

          {sortedRecords?.length > 2 && (
            <>
              <Collapse in={expandAll} timeout="auto" unmountOnExit>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                  {sortedRecords.slice(2).map((record) => (
                    <Paper
                      key={record.id}
                      sx={{
                        p: 1.25,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                      data-testid={`billing-item-${record.id}`}
                      data-service-code={record.service_code}
                      data-billing-status={record.status}
                      data-service-date={record.service_date}
                      data-provider={record.provider}
                      data-amount={record.amount}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight={600}>
                            {record.service_code || "Unknown Code"} • ${parseFloat(record.amount || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {record.description || "Service"} • {record.service_date ? record.service_date.split("T")[0] : "N/A"}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={0.5} alignItems="center">
                          <Chip
                            size="small"
                            label={record.status || "pending"}
                            color={getBillingStatusColor(record.status)}
                            variant="outlined"
                            data-testid={`billing-status-chip-${record.id}`}
                            data-field-name="status"
                          />
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, record.id)}
                              data-testid={`billing-menu-${record.id}`}
                              aria-label="Billing record options menu"
                            >
                              <MoreVertIcon aria-label="Billing record options menu" fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Collapse>

              <Button
                size="small"
                onClick={() => setExpandAll(!expandAll)}
                startIcon={expandAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: "none", mt: 1 }}
              >
                {expandAll ? "View Less" : `View All (${sortedRecords.length - 2} more)`}
              </Button>
            </>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No billing records
        </Typography>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => handleDelete(activeBillingId)}
          data-testid={`billing-menu-delete-${activeBillingId}`}
          data-action="delete"
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

BillingRecordsCard.propTypes = {
  apiBase: PropTypes.string,
  patientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onBillingAdded: PropTypes.func,
  billingRecords: PropTypes.array,
  onDeleteBillingRecord: PropTypes.func,
  onAddBillingRecord: PropTypes.func,
};

BillingRecordsCard.defaultProps = {
  billingRecords: [],
  onDeleteBillingRecord: null,
  onAddBillingRecord: null,
};

export default BillingRecordsCard;
